import { useSearchParams, Link } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import { useCheckoutSession } from '../hooks/useBilling';
import Spinner from '../components/common/Spinner';
import { BRAND } from '../utils/brand';
import { isUispCheckoutPlan } from '../utils/subscription';
import type { CheckoutSession } from '../types';

export default function BillingSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const { data: session, isLoading, error } = useCheckoutSession(sessionId);

  // No session ID - redirect to billing
  if (!sessionId) {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <p className="text-gray-600 mb-4">No checkout session found.</p>
          <Link
            to="/billing"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Go to Billing
          </Link>
        </div>
      </AppLayout>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <Spinner size="lg" />
          <p className="text-gray-600 mt-4">Verifying your payment...</p>
        </div>
      </AppLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <svg
              className="w-12 h-12 text-red-500 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h1 className="text-xl font-semibold text-red-800 mb-2">
              Verification Failed
            </h1>
            <p className="text-red-700 mb-4">
              We couldn't verify your checkout session. Please contact support
              if you completed payment.
            </p>
            <Link
              to="/billing"
              className="inline-block px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
            >
              Return to Billing
            </Link>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Pending/Processing state
  if (session?.status !== 'complete') {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
            <svg
              className="w-12 h-12 text-yellow-500 mx-auto mb-4 animate-pulse"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h1 className="text-xl font-semibold text-yellow-800 mb-2">
              Processing Payment
            </h1>
            <p className="text-yellow-700 mb-4">
              {session?.message ||
                'Your payment is still being processed. This may take a moment.'}
            </p>
            <p className="text-sm text-yellow-600 mb-4">
              If this takes more than a minute, please contact support.
            </p>
            <Link
              to="/billing"
              className="inline-block px-4 py-2 border border-yellow-600 text-yellow-700 hover:bg-yellow-100 font-medium rounded-lg transition-colors"
            >
              Return to Billing
            </Link>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Success state - determine if migration or new subscription
  const isMigration = session.type === 'migration';

  const formatCurrency = (amount: number, currency?: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount / 100); // Stripe amounts are in cents
  };

  const formatBillingPeriod = (
    interval: NonNullable<CheckoutSession['interval']>,
    count?: number | null
  ) => {
    if (!count || count === 1) {
      return { day: 'Daily', week: 'Weekly', month: 'Monthly', year: 'Annually' }[
        interval
      ];
    }
    return `Every ${count} ${interval}s`;
  };

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          {/* Success Icon */}
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            {isMigration ? 'Migration Complete!' : 'Subscription Activated!'}
          </h1>

          <p className="text-gray-600 mb-6">
            {isMigration
              ? 'Your billing has been successfully migrated to Stripe.'
              : `Your ${BRAND.name} subscription is now active.`}
          </p>

          {/* Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <dl className="space-y-3">
              {session.plan_name && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Plan</dt>
                  <dd className="font-medium text-gray-900">
                    {session.plan_name}
                  </dd>
                </div>
              )}

              {/* amount is Stripe's amount_total: the whole first charge after
                  discounts, not a recurring rate (an annual plan's is a year's
                  price). It is 0 on a $0 checkout, so check for a number rather
                  than truthiness (a bare 0 renders as text). */}
              {typeof session.amount === 'number' && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Amount paid</dt>
                  <dd className="font-medium text-gray-900">
                    {formatCurrency(session.amount, session.currency)}
                  </dd>
                </div>
              )}

              {session.interval && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Billing</dt>
                  <dd className="font-medium text-gray-900">
                    {formatBillingPeriod(session.interval, session.interval_count)}
                  </dd>
                </div>
              )}

              {isMigration && session.previous_provider && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Previous Provider</dt>
                  <dd className="font-medium text-gray-900">
                    {session.previous_provider} (cancelled)
                  </dd>
                </div>
              )}

              {session.customer_email && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Confirmation sent to</dt>
                  <dd className="font-medium text-gray-900">
                    {session.customer_email}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* What's Next */}
          <div className="text-left mb-6">
            <h2 className="font-medium text-gray-900 mb-2">What's next:</h2>
            <ul className="text-sm text-gray-600 space-y-1">
              {isMigration ? (
                <>
                  <li className="flex items-start">
                    <svg
                      className="w-5 h-5 text-green-500 mr-2 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Your old {session.previous_provider} subscription has been
                    cancelled
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="w-5 h-5 text-green-500 mr-2 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    You can now manage billing through Stripe
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="w-5 h-5 text-green-500 mr-2 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    A confirmation email has been sent to your address
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start">
                    <svg
                      className="w-5 h-5 text-green-500 mr-2 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {isUispCheckoutPlan(session.plan_name)
                      ? 'Your UISP is being set up'
                      : 'Your controller is being provisioned'}
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="w-5 h-5 text-green-500 mr-2 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    You'll receive an email when it's ready
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="w-5 h-5 text-green-500 mr-2 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Check the Services page for status updates
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/billing"
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              Go to Billing
            </Link>
            <Link
              to="/services"
              className="px-6 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-colors"
            >
              View Services
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
